import { useState } from "react";
import { Mail, Phone, MapPin, Clock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import DemoModal from "@/components/DemoModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  { icon: Mail, label: "Email Us", value: "nexttackle@gmail.com" },
  { icon: Phone, label: "Call Us", value: "+49 15783236245" },
  { icon: MapPin, label: "Our Office", value: "Frankfurt, Germany" },
  { icon: Clock, label: "Response Time", value: "24 hours" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [demoOpen, setDemoOpen] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="bg-gray-900 pt-32 pb-20 text-center" data-testid="contact-page-header">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-xs uppercase tracking-widest text-forest/70 font-bold mb-4">
            Get In Touch
          </p>
          <h1
            className="text-5xl md:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            We'd Love to <span className="text-forest/70">Hear From You</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Whether you have a question, feedback, or want to see NextTackle in action — we're here.
          </p>
        </div>
      </div>

      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="space-y-8" data-testid="contact-info">
              <div>
                <h2
                  className="text-2xl font-black text-gray-900 mb-6"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Contact Information
                </h2>
              </div>
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-forest/5 rounded-sm flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">
                      {item.label}
                    </div>
                    <div className="text-gray-900 font-medium text-sm">{item.value}</div>
                  </div>
                </div>
              ))}

              <div className="bg-forest/5 border border-green-200 rounded-sm p-6 mt-8">
                <h3
                  className="font-bold text-gray-900 mb-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Want a Demo?
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Book a 30-minute personalized demo and see NextTackle in action.
                </p>
                <Button
                  onClick={() => setDemoOpen(true)}
                  className="w-full bg-forest hover:bg-forest/90 text-white rounded-sm text-sm"
                  data-testid="contact-demo-btn"
                >
                  Book Demo
                </Button>
              </div>
            </div>

            <div className="lg:col-span-2" data-testid="contact-form-wrapper">
              {success ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <CheckCircle className="w-16 h-16 text-forest mb-5" />
                  <h3
                    className="text-2xl font-black text-gray-900 mb-3"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    Message Sent!
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    Thank you for reaching out. We typically respond within 24 hours.
                  </p>
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setForm({ name: "", email: "", subject: "", message: "" });
                    }}
                    variant="outline"
                    className="rounded-sm"
                    data-testid="contact-send-another-btn"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="contact-name">Full Name *</Label>
                      <Input
                        id="contact-name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="John Smith"
                        required
                        data-testid="contact-name-input"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email">Email Address *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="john@academy.com"
                        required
                        data-testid="contact-email-input"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="contact-subject">Subject *</Label>
                    <Input
                      id="contact-subject"
                      value={form.subject}
                      onChange={update("subject")}
                      placeholder="How can we help?"
                      required
                      data-testid="contact-subject-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us about your academy and how we can help..."
                      required
                      data-testid="contact-message-input"
                      rows={6}
                      className="mt-1 resize-none"
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-red-600" data-testid="contact-error">
                      {error}
                    </p>
                  )}
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="bg-forest hover:bg-forest/90 text-white rounded-sm px-8 h-auto py-3"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
